import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Button, Divider, Flex, Title } from '@mantine/core';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';

import { ControllingPartyEntryFields } from './ControllingPartyEntryFields';
import {
	createEmptyControllingParty,
	getDuplicateControllingPartyIndices,
} from './controllingParties.utils';
import { JOIN_ALLOCATION_PIV_INFO_FORM } from './useJoinAndAllocationPIVInformationController';
import { PIVInfoForm } from './useJoinAndAllocationPIVInformationController.types';

interface Props {
	disabled?: boolean;
}

export const ControllingPartiesSection = ({ disabled }: Props) => {
	const tInvestor = useTranslationInvestor();
	const { control } = useFormContext<PIVInfoForm>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES,
	});

	const watchedParties =
		useWatch({ control, name: JOIN_ALLOCATION_PIV_INFO_FORM.CONTROLLING_PARTIES }) ?? [];
	const duplicateIndices = getDuplicateControllingPartyIndices(watchedParties);

	return (
		<Flex direction="column" gap="1rem">
			<div>
				<Title order={5} mb="1rem">
					{tInvestor('investment.piv.controlling_parties.title')}
				</Title>

				<Divider pt="1rem" pb="0.5rem" c="gray.2" />

				<InfoBox
					iconVariant="information"
					description={tInvestor('investment.piv.controlling_parties.description')}
				/>
			</div>

			{fields.map((field, index) => (
				<ControllingPartyEntryFields
					key={`${field.id}-${index}`}
					index={index}
					disabled={disabled}
					isDuplicate={duplicateIndices.has(index)}
					onRemove={() => remove(index)}
				/>
			))}

			<div>
				<Button
					variant="default"
					onClick={() => append(createEmptyControllingParty())}
					disabled={disabled}
				>
					{tInvestor('investment.piv.controlling_parties.add')}
				</Button>
			</div>
		</Flex>
	);
};
